import { useDispatch } from 'react-redux';

import { Alert, Box, Button, Card, Heading, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import type { CreateRenterPaymentMethodWebApiResponseDto } from '@zg-rentals/ts-rent-payment-client';
import { useDeletePaymentMethodWebApiHandler_deletePaymentMethod } from '@zg-rentals/ts-rent-payment-client-react-query';

import { useCreateRenterPaymentMethod } from '../../../hooks/useCreatePaymentMethod';
import { getFormattedPaymentMethod } from '../../../../common/constants/PaymentMethod';
import { VERIFICATION_STATUS } from '../../../../common/constants/PlaidRequirement';
import { formatDate } from '../../../../common/paymentsUtils/dateUtils';
import type { RenterPaymentMethodInterface } from '../../../models/Types';

type Props = {
  duplicatePaymentMethod: RenterPaymentMethodInterface;
  pendingPaymentInfo: any;
  isPendingDataSave: boolean;
  setIsPendingDataSave: (isPendingDataSave: boolean) => void;
  onPaymentMethodCreationSuccess: (paymentMethod: CreateRenterPaymentMethodWebApiResponseDto) => void;
  onPaymentMethodCreationError: (error: unknown) => void;
};

const DuplicatePaymentMethodWarning = ({
  duplicatePaymentMethod,
  isPendingDataSave,
  pendingPaymentInfo,
  setIsPendingDataSave,
  onPaymentMethodCreationSuccess,
  onPaymentMethodCreationError,
}: Props) => {
  const dispatch = useDispatch();

  const { mutateAsync: createRenterPaymentMethod } = useCreateRenterPaymentMethod({
    onSuccess: (paymentMethod) => {
      setIsPendingDataSave(false);
      onPaymentMethodCreationSuccess(paymentMethod);
    },
    onError: (error) => {
      setIsPendingDataSave(false);
      onPaymentMethodCreationError(error);
    },
  });

  const { mutateAsync: deletePaymentMethod } = useDeletePaymentMethodWebApiHandler_deletePaymentMethod();

  const handleDuplicatePaymentMethod = async ({
    duplicatePaymentMethodId,
    pendingPaymentInfo,
  }: {
    duplicatePaymentMethodId?: string;
    pendingPaymentInfo: any;
  }) => {
    if (duplicatePaymentMethodId) {
      deletePaymentMethod({ postData: { encodedPaymentMethodId: duplicatePaymentMethod.id } });
    }

    await createRenterPaymentMethod({
      postData: {
        bankName: pendingPaymentInfo.bankName,
        mask: pendingPaymentInfo.mask,
        ...(pendingPaymentInfo.status && { status: pendingPaymentInfo.status }),
        plaidToken: pendingPaymentInfo.plaidToken,
      },
    });
  };
  return (
    <Box>
      <Box>
        <Alert role="status" tone="warning" css={{ padding: 'default' }}>
          It looks like you’ve already added this payment method.
        </Alert>
        <Box
          css={{
            display: 'flex',
            gap: 'default',
            justifyContent: 'space-between',
            marginTop: 'loose',
            marginBottom: 'loose',
          }}
        >
          <Card elevated={false} css={{ padding: 'loose', flex: 1 }}>
            <Paragraph textStyle="body-bold" css={{ marginBottom: 'default' }}>
              Existing
            </Paragraph>
            <Heading level={2} textStyle="heading-xs">
              {duplicatePaymentMethod.nickname || duplicatePaymentMethod?.displayName}
            </Heading>
            <Paragraph>{`${getFormattedPaymentMethod(
              duplicatePaymentMethod.accountType || '',
            )} ending in ${duplicatePaymentMethod?.accountLast4}`}</Paragraph>
            <Paragraph>
              Status:{' '}
              <Text css={{ color: 'text.feedback.critical.strong' }}>
                {duplicatePaymentMethod.paymentMethodStatus === VERIFICATION_STATUS.PENDING_MANUAL.INTERNAL
                  ? 'Pending manual verification'
                  : 'Pending automatic verification'}
              </Text>
            </Paragraph>
            <Paragraph>Added: {formatDate(duplicatePaymentMethod.createdAt, 'MM/dd/yyyy')}</Paragraph>
          </Card>
          <Card elevated={false} css={{ padding: 'loose', flex: 1 }}>
            <Paragraph textStyle="body-bold" css={{ marginBottom: 'default' }}>
              New
            </Paragraph>
            <Heading level={2} textStyle="heading-xs">
              {pendingPaymentInfo?.bankName}
            </Heading>
            <Paragraph>{`Bank ending in ${pendingPaymentInfo?.mask}`}</Paragraph>
            <Paragraph>
              Status:{' '}
              <Text css={{ color: 'text.feedback.warning.strong' }}>
                {pendingPaymentInfo?.status === VERIFICATION_STATUS.PENDING_MANUAL.PLAID
                  ? 'Pending manual verification'
                  : 'Pending automatic verification'}
              </Text>
            </Paragraph>
            <Paragraph>Added: Today</Paragraph>
          </Card>
        </Box>
        <Paragraph css={{ marginBottom: 'loose' }}>
          <Text textStyle="body-bold">Would you like to replace it?</Text> This will restart the verification process.
          Otherwise, keep both in your account.
        </Paragraph>
      </Box>
      <Box
        css={{
          display: 'flex',
          gap: 'default',
          justifyContent: 'flex-end',
          marginTop: 'loose',
        }}
      >
        <TextButton
          onClick={async () => {
            setIsPendingDataSave(true);
            await handleDuplicatePaymentMethod({ pendingPaymentInfo });
            setIsPendingDataSave(false);
          }}
          disabled={isPendingDataSave}
        >
          Keep both
        </TextButton>
        <Button
          onClick={async () => {
            setIsPendingDataSave(true);
            await handleDuplicatePaymentMethod({
              duplicatePaymentMethodId: duplicatePaymentMethod.id,
              pendingPaymentInfo,
            });
            setIsPendingDataSave(false);
          }}
          disabled={isPendingDataSave}
          emphasis="filled"
        >
          Replace
        </Button>
      </Box>
    </Box>
  );
};

export default DuplicatePaymentMethodWarning;
